import { useModal } from '@/components/modal/useModal';
import ManageCustomerUsersModal from '@/pages/msps/customers/ManageCustomerUsersModal';
import ManageMspUsersModal from '@/pages/msps/ManageMspUsersModal';
import { useCompany } from '@/services/api/hooks/companies';
import { RoleType, useUser } from '@/services/authentication/hooks/user';
import { Menu } from '@headlessui/react';
import { FC, PropsWithChildren } from 'react';
import { FiSettings } from 'react-icons/fi';
import { InviteAdminModal } from '../InviteAdminModal';
import { InviteCreditCheckerModal } from '../InviteCreditCheckerModal';
import SettingsLink from './SettingsLink';
import SubscriptionSettingLink from './SubscriptionSettingLink';

const PartnerSettings: FC = () => {
  const { role } = useUser();
  const manageMspUsersModalProps = useModal<{ mspId: string }>();
  const onMspManageUsers = () => manageMspUsersModalProps.open({ mspId: role.partnerId! });

  return (
    <>
      <ManageMspUsersModal {...manageMspUsersModalProps} />
      <SettingsContainer>
        <SettingsLink name="Manage Users" onClick={onMspManageUsers} />
        <SubscriptionSettingLink />
      </SettingsContainer>
    </>
  );
};

const CustomerSettings: FC = () => {
  const { role, id } = useUser();
  const [company] = useCompany(role.partnerId!, role.customerId!);

  const canManageUsers = !company.disabled && company.signatoryId === id;

  const manageUsersModalProps = useModal<{ mspId: string; customerId: string; asCustomer: boolean }>();
  const onCustomerManageUsers = () =>
    manageUsersModalProps.open({ mspId: role.partnerId!, customerId: role.customerId!, asCustomer: true });
  return (
    <>
      <ManageCustomerUsersModal {...manageUsersModalProps} />
      {canManageUsers && (
        <SettingsContainer>
          <SettingsLink name="Authorised Agents" onClick={onCustomerManageUsers} />
        </SettingsContainer>
      )}
    </>
  );
};

const AdminSettings: FC = () => {
  const inviteAdminModalProps = useModal();
  const inviteCreditCheckerModalProps = useModal();

  return (
    <>
      <InviteAdminModal {...inviteAdminModalProps} />
      <InviteCreditCheckerModal {...inviteCreditCheckerModalProps} />
      <SettingsContainer>
        {/*<SettingsLink name="Approvals" to="/approvals" />*/}
        <SettingsLink name="Credit Check" to="/mspcreditcheck" />
        <SettingsLink name="Finance Models" to="/financeModels" />
        <SettingsLink name="Invite an admin" onClick={() => inviteAdminModalProps.open()} />
        <SettingsLink name="Invite a credit checker" onClick={() => inviteCreditCheckerModalProps.open()} />
        <SubscriptionSettingLink />
      </SettingsContainer>
    </>
  );
};

const SettingsContainer: FC<PropsWithChildren> = ({ children }) => {
  return (
    <Menu>
      <Menu.Button as="button" className="flex items-center justify-center gap-2">
        <div className="relative">
          <div className="bg-primary-100 flex h-10 w-10 items-center justify-center rounded-full">
            <FiSettings className="text-primary-700 h-6 w-6" />
          </div>
          <Menu.Items className="absolute z-50 right-0 mt-2 min-w-fit divide-y divide-gray-100 rounded-md border border-gray-200 bg-white shadow-lg outline-none">
            {children}
          </Menu.Items>
        </div>
      </Menu.Button>
    </Menu>
  );
};

export const Settings: FC = () => {
  const { role } = useUser();

  return (
    <>
      {role.type === RoleType.Admin && <AdminSettings />}
      {role.type === RoleType.Partner && <PartnerSettings />}
      {role.type === RoleType.Company && <CustomerSettings />}
    </>
  );
};
